import { useCallback, useEffect, useRef, useState } from 'react';

import { isClientSide } from '@r1-frontend/shared/helpers/ssr';

export interface IHeaderScrollProps {
    offset?: number,
}

const useScrollingUpState = (offset: number) => {
    const prevScrollY = useRef(isClientSide() ? window.scrollY : 0);
    const [isScrollingUp, setIsScrollingUp] = useState(() => prevScrollY.current <= offset);

    const updateScrollDirection = useCallback(() => {
        const scrollY = window.scrollY;

        if (Math.abs(scrollY - prevScrollY.current) >= offset) {
            const newScrollDirection = scrollY < prevScrollY.current && scrollY <= offset;
            const newPrevScrollY = scrollY > 0 ? scrollY : 0;

            if (newScrollDirection !== isScrollingUp) {
                setIsScrollingUp(newScrollDirection);
            }

            if (newPrevScrollY !== prevScrollY.current) {
                prevScrollY.current = newPrevScrollY;
            }
        }
    }, [offset, isScrollingUp]);

    return { isScrollingUp, updateScrollDirection };
};

export const useHeaderScroll = ({ offset = 100 }: IHeaderScrollProps = {}) => {
    const { isScrollingUp, updateScrollDirection } = useScrollingUpState(offset);

    const blocking = useRef(false);

    useEffect(() => {
        const onScroll = () => {
            if (!blocking.current) {
                blocking.current = true;
                window.requestAnimationFrame(updateScrollDirection);
                blocking.current = false;
            }
        };

        window.addEventListener('scroll', onScroll);

        return () => window.removeEventListener('scroll', onScroll);
    }, [updateScrollDirection]);

    return { isScrollingUp };
};
